

























































































import { Component, Mixins, Watch } from "vue-property-decorator";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import UpgradeDowngradeInjectorMixin from "@/mixins/upgradeDowngradeInjectorMixin";

// Types
import { IProductConfig } from "@/models/providers/products";
import { IInvoice } from "@/models/invoices";
import { IContractProduct } from "@/models/contracts";
import CProdMixin from "@/mixins/cProdMixin";

@Component({
  components: {
    "cprod-migration-image": () =>
      import(
        "@/components/app/global/contractProducts/cProdMigrationImage.vue"
      ),
    "product-config-provider": () =>
      import("@/components/app/global/baskets/basket/productConfigProvider"),
    "product-options": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productOptions.vue"
      ),
    "product-attributes": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productAttributes.vue"
      )
  }
})
export default class UpgradeDowngradeForm extends Mixins(
  HasValidation,
  UpgradeDowngradeInjectorMixin,
  CProdMixin
) {
  // Data

  public isProcessingDryRun = false;
  public isProcessing = false;
  public readMore = false;
  public charLimit = 150;

  public productConfiguration = {} as IProductConfig;
  public invoicePreview: IInvoice | null = null;

  public productConfigurationLoaded = false;

  // Methods

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;
      const invoice = await this.$upgradeDowngradeMethods.changeProduct(
        this.configuration
      );
      this.$store.dispatch("toast/show", {
        message: this.$t("_.product_changed")
      });
      this.$emit("success", invoice);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.isProcessing = false;
    }
  }

  public async doDryRun() {
    try {
      this.isProcessingDryRun = true;
      this.invoicePreview = await this.$upgradeDowngradeMethods.changeProduct({
        ...this.configuration,
        dry_run: true
      });
    } catch {
      // Silently fail
    } finally {
      this.isProcessingDryRun = false;
    }
  }

  public computeConfigPrice(
    customPrice: number | undefined,
    contractPrice?: IContractProduct["selling_price"],
    totalPrice?: number
  ) {
    let computedPrice: number | undefined = undefined;
    // 1st: Favour any custom price (if specified)
    if (_.isNumber(customPrice)) computedPrice = customPrice;
    // 2nd: If config total differs from existing price, use new total
    else if (contractPrice !== totalPrice) computedPrice = totalPrice;
    // Return new price if different to existing contract price
    return computedPrice === contractPrice ? undefined : computedPrice;
  }

  // Computed

  public get productConfigProps() {
    const {
      contract,
      contractProduct,
      migrationProduct
    } = this.upgradeDowngradeData;
    return {
      accountId: contract?.account_id,
      billingTerm: contractProduct?.billing_cycle_months,
      currencyId: contract?.currency_id,
      formatConfigTotal: false, // Prevent unnecessary 'calculate' calls in migration context
      omitPromotions: true, // We don't care for promotions when migrating
      product: migrationProduct,
      taxType: contract.tax_type
    };
  }

  public get configuration() {
    return {
      contract_id: this.upgradeDowngradeData.contract.id,
      contracts_product_id: this.upgradeDowngradeData.contractProduct.id,
      product: {
        product_id: this.upgradeDowngradeData.migrationProduct.id,
        billing_cycle_months: _.get(
          this.productConfiguration,
          "billing_cycle_months"
        )
      },
      options: this.options,
      attributes: this.attributes
    };
  }

  public get options() {
    return _.map(_.get(this.productConfiguration, "options", []), option => {
      const oldOption = _.find(
        this.upgradeDowngradeData.contractProduct?.options,
        ["product_id", option.product_id]
      );
      return {
        product_id: option.product_id,
        billing_cycle_months: option.billing_cycle_months,
        unit_quantity: option.unit_quantity,
        price: this.computeConfigPrice(
          option?.selling_price,
          oldOption?.selling_price,
          option?.unit_total
        )
      };
    });
  }

  public get attributes() {
    return _.map(_.get(this.productConfiguration, "attributes", []), attr => ({
      product_id: attr.product_id
    }));
  }

  public get isEnabled() {
    return (
      this.productConfigurationLoaded &&
      !this.hasErrors &&
      !this.isProcessingDryRun
    );
  }

  public get proRataAmount() {
    return Math.abs(this.invoicePreview?.total_amount_converted ?? 0);
  }

  public get oldProduct() {
    return this.upgradeDowngradeData.contractProduct?.product;
  }

  public get oldProductName() {
    return this.getProductName(this.upgradeDowngradeData.contractProduct);
  }

  public get newProduct() {
    return this.upgradeDowngradeData.migrationProduct;
  }

  public get newProductName() {
    return this.newProduct?.name_translated || this.newProduct?.name;
  }

  // Watchers

  @Watch("configuration", { deep: true })
  public onConfigurationChange(newVal, oldVal) {
    if (_.isEqual(newVal, oldVal) || !this.productConfigurationLoaded) return;
    this.invoicePreview = null;
    this.$nextTick(async () => {
      if (!(await this.validate())) return;
      this.doDryRun();
    });
  }

  @Watch("isEnabled")
  public onIsEnabledChange() {
    this.$emit("isEnabled", this.isEnabled);
  }

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessing", this.isProcessing);
  }
}
